import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  GitBranch,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Phase 3 piece targeting "AI vs rule-based file organizer" and "Sortio AI
 * Sort vs Rule Builder". Unique to Sortio since it ships both modes. Helps
 * users self-select; pulls from the memory note that AI sort works best
 * under ~5000 files and that AI Rule Builder is the right tool for very
 * large drives. Cross-links to glossary entries and to the rules-engine docs.
 */

const PUBLISHED = '2026-05-14';
const CANONICAL = 'https://www.getsortio.com/blog/ai-sort-vs-rule-builder-which-to-use';

const FAQ = [
  {
    question: 'What is the difference between AI Sort and AI Rule Builder in Sortio?',
    answer:
      'AI Sort takes a plain-English prompt and uses an LLM to make a per-file routing decision. Every file gets read by the model and routed individually. AI Rule Builder takes a plain-English description and generates a deterministic rule (extension, filename pattern, metadata) that runs without an LLM. AI Sort is best for small batches and ambiguous content; Rule Builder is best for large drives and predictable patterns.',
  },
  {
    question: 'When does AI Sort stop working well?',
    answer:
      'AI Sort works best on batches under about 5,000 files. Above that, inference time gets long enough that you would rather wait for a deterministic rule to run, and very large runs draw more from your AI allowance on the managed cloud tier. The recommended workflow for large drives is to run AI Sort on a representative sample to figure out the right rules, then promote them to AI Rule Builder, then run the rules over the full drive.',
  },
  {
    question: 'Does AI Rule Builder use my AI allowance?',
    answer:
      'Rule generation uses a small part of your AI allowance (the AI describes-to-rule step). Rule execution does not use your allowance at all: once the rule exists, it runs deterministically on your machine for free, no matter how many files. This is why Rule Builder is the right tool for large drives: the AI step happens once and the rule handles the volume.',
  },
  {
    question: 'Can I mix AI Sort and rules in the same sort run?',
    answer:
      'Yes. Sortio applies rules first (deterministic, fast) and uses AI Sort to handle anything the rules did not match. This is the most efficient workflow for mixed folders: rules catch 80 percent of the predictable cases, AI handles the long tail. You can also stack the rules: first rule routes screenshots, second routes installers, third routes invoices, AI catches whatever did not match.',
  },
  {
    question: 'When is AI Sort clearly the right tool?',
    answer:
      'Three situations. First, ambiguous content where the routing decision needs to read the file (an invoice that does not say "invoice" in the filename, a contract that could be filed under two counterparties). Second, small batches where you only run the sort once or twice and a rule is not worth building. Third, exploratory sorts where you do not yet know the patterns in your data: AI Sort surfaces them so you can decide what rules to build.',
  },
  {
    question: 'When is Rule Builder clearly the right tool?',
    answer:
      'Three situations. Large drives (10,000+ files) where AI inference would be slow and expensive. Stable patterns where the routing decision is the same every time (Screenshots go to ~/Pictures/Screenshots, period). Recurring workflows where a rule needs to run unattended for years (watch folders for a project, monthly archive sweeps).',
  },
  {
    question: 'How do I promote an AI Sort into a rule?',
    answer:
      'After an AI Sort run, the right-click menu on any routing decision lets you "Make this a rule." Sortio passes the prompt context to the Rule Builder, which converts the natural-language description into a deterministic rule and lets you preview it. If the preview matches what AI Sort did, you save the rule and it runs on future sorts without using your AI allowance.',
  },
];

const AiSortVsRuleBuilderBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'AI Sort vs Rule Builder: which to use', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'AI Sort vs Rule Builder: which to use',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'AI Sort vs Rule Builder in Sortio: Which One Should You Use?',
    description:
      'Sortio has two organizing modes. AI Sort uses an LLM per file; Rule Builder generates deterministic rules. A practical guide on when each is the right tool, with examples from real workflows.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="AI Sort vs Rule Builder in Sortio: Which One Should You Use?"
        description="Sortio ships two organizing modes. AI Sort runs an LLM per file; Rule Builder generates deterministic rules. When to pick each one, with examples from real workflows on small and large drives."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="ai vs rule-based file organizer, sortio ai sort vs rule builder, ai rule builder, rule based file sorter, ai file organizer small files large drive"
        lastModified={PUBLISHED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            AI Sort vs Rule Builder in Sortio: Which One Should You Use?
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>8 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Sortio has two ways to organize a folder, and the most common
            new-user question is "which one do I use?" Both are AI-driven, both
            take a plain-English description of what you want, and both produce
            organized output. They make a different trade-off, and getting the
            choice wrong costs either time or money.
          </p>

          <p>
            This post is the practical guide. What each mode is, how they
            behave on small folders vs. large drives, how the cost model
            differs, and a decision rubric for picking the right one. The
            short answer is at the top, the nuance is below.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <GitBranch className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  AI Sort runs an LLM per file. Best for batches under ~5,000
                  files, ambiguous content, and one-off sorts. Rule Builder
                  generates a deterministic rule once and runs it forever for
                  free. Best for large drives, predictable patterns, and
                  watch-folder automation. Most heavy users run both: AI Sort
                  to discover the patterns, Rule Builder to run them at scale.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            What AI Sort does
          </h2>

          <p>
            AI Sort takes a prompt like "sort my Downloads folder. Invoices go
            to ~/Documents/Finance, screenshots go to ~/Pictures/Screenshots,
            installers go to the Trash." Sortio iterates the folder, reads each
            file&apos;s content (PDF text layer, document body) plus filename
            and metadata, sends it to an LLM with the prompt as context, and
            the model returns a routing decision per file. Sortio shows the proposed moves in a
            preview, you confirm, and the sort runs.
          </p>

          <p>
            The strength is intelligence per file. The model understands what
            an invoice looks like even if the filename does not say "invoice."
            It picks the right vendor name out of a noisy header.
            For folders where every file is different and the routing decision
            depends on reading the file, AI Sort is the right tool.
          </p>

          <p>
            The cost is inference time and AI usage. Every file you sort
            draws a small amount from your AI allowance on the managed AI
            tier, or some local inference time if you run Ollama. For a
            100-file folder this is invisible. For a 50,000-file drive this is
            a real cost in both time and allowance.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            What Rule Builder does
          </h2>

          <p>
            Rule Builder takes the same kind of description ("move every PNG
            that starts with Screen Shot to ~/Pictures/Screenshots") and
            instead of running inference per file, it generates a
            deterministic rule. The rule is a small structured config (match
            by extension, by filename pattern, by metadata, by date) that
            Sortio executes directly without calling an LLM.
          </p>

          <p>
            The strength is speed and cost. A rule sorts 50,000 files in
            seconds. There is no per-file inference, no AI allowance used, no
            roundtrip to an AI provider. Once the rule exists, it runs the same
            way forever, even if you go offline. It is the right tool when the
            pattern is stable and you want it to run unattended.
          </p>

          <p>
            The cost is up-front design and limited intelligence. Rules see
            file metadata and filename patterns. They do not read PDF content
            and they do not understand "is this a contract." If the pattern is more sophisticated than what
            metadata can express, a rule cannot do it; you need AI Sort.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The 5,000-file threshold
          </h2>

          <p>
            Sortio&apos;s honest guidance from a few thousand real-user sort
            runs: AI Sort works best on batches under about 5,000 files. Above
            that, three things happen that push the workflow toward Rule
            Builder.
          </p>

          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Time.</strong> 10,000-file AI sorts take 15+ minutes on
              cloud and longer locally. You can wait, but it is not the
              instant feedback that makes Sortio useful.
            </li>
            <li>
              <strong>Cost.</strong> A 10,000-plus-file run uses a real share
              of the free trial allowance. Pro&apos;s monthly AI allowance is
              sized for heavy use, but running AI per file when a rule would
              do is still the expensive way to sort a whole drive.
            </li>
            <li>
              <strong>Repetition.</strong> Large drives usually have a long
              tail of similar files (a folder of 30,000 photos, a folder of
              50,000 documents). Running AI per file when the routing decision
              is the same for every file is wasteful.
            </li>
          </ul>

          <p>
            Above the threshold the right workflow is: run AI Sort on a
            representative sample (a few hundred files), see what patterns the
            model is using, promote those patterns to rules, then let the
            rules sweep the rest of the drive. This is the same idea as
            sample-and-script in data work: use the expensive intelligent tool
            to find the structure, then apply a cheap deterministic tool at
            scale.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Three real workflows
          </h2>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            Workflow 1: Downloads folder cleanup (AI Sort)
          </h3>
          <p>
            Folder size: 1,500 files. Content: invoices, receipts, installers,
            ZIPs, miscellaneous downloads. The routing decisions need content
            reading (an invoice from a new vendor has no filename pattern; an
            old ZIP is not obviously stale from metadata alone). AI Sort is
            the right tool. One prompt, one run, done in two minutes. See the
            full walkthrough in our piece on{' '}
            <Link
              to="/blog/keep-mac-downloads-folder-clean"
              className="text-sortio-primary hover:underline"
            >
              keeping your Mac Downloads folder clean
            </Link>
            .
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            Workflow 2: 30,000-photo migration (Rule Builder)
          </h3>
          <p>
            Folder size: 30,000 photos exported from Lightroom, all with
            consistent EXIF metadata. The routing decision is "by date, then
            by camera." This is a pure metadata sort: Rule Builder generates
            a deterministic rule that reads EXIF and routes by year/month/
            camera in seconds. AI Sort would work too but would be slow and
            wasteful. Rule Builder is the right tool.
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            Workflow 3: 8,000 mixed legal documents (both)
          </h3>
          <p>
            Folder size: 8,000 PDFs from a small-firm legal practice. About
            70 percent are predictable (deposition transcripts named by
            convention, court filings with a consistent docket pattern). 30
            percent are not (random vendor invoices, scanned client documents,
            misc correspondence). The right workflow is two-pass: Rule Builder
            handles the 70 percent that have a stable pattern, AI Sort handles
            the 30 percent that do not. Both run in the same sort, rules first.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A decision rubric
          </h2>

          <p>
            Pick AI Sort when at least one of these is true:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>The folder has fewer than 5,000 files.</li>
            <li>
              The routing decision needs the file&apos;s content (you cannot
              decide by filename and metadata alone).
            </li>
            <li>This is a one-off sort you will not repeat.</li>
            <li>You do not yet know the patterns in your data.</li>
          </ul>

          <p>
            Pick Rule Builder when at least one of these is true:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>The folder has 5,000+ files.</li>
            <li>
              The routing decision is the same every time and depends on
              metadata or filename pattern.
            </li>
            <li>
              You want this to run unattended (watch folder).
            </li>
            <li>You want to minimize AI usage.</li>
          </ul>

          <p>
            Run both when:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              The folder is mixed: some files have stable patterns, others do
              not. Rules handle the easy cases, AI Sort handles the long tail.
            </li>
            <li>
              You are migrating a large archive: AI Sort to discover patterns
              on a sample, Rule Builder to execute at scale.
            </li>
          </ul>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Cost considerations
          </h2>

          <p>
            Sortio Free includes a one-time AI trial allowance and rule-based
            sorting up to 5 rules. Pro at $14.99/month or $99/year gives a
            monthly AI allowance sized for heavy use (no Sortio user has ever
            used half of it) and unlimited rules, plus watch folders and
            on-change automations. For most users the allowance is plentiful;
            the move to Pro is usually driven by the watch-folder and
            automation features, not by hitting the usage limit.
          </p>

          <p>
            If you run high-volume AI sorts and want predictable cost, the
            Ollama local mode is never metered by Sortio (electricity aside).
            See our piece on{' '}
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="text-sortio-primary hover:underline"
            >
              local AI vs cloud AI
            </Link>{' '}
            for the speed and accuracy trade-off.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Promoting an AI Sort to a rule
          </h2>

          <p>
            The most efficient long-term workflow is to start with AI Sort,
            then promote successful patterns to rules. After a sort run, open
            the activity log, right-click any decision, and choose "Make this
            a rule." Sortio passes the natural-language description to the
            Rule Builder, which produces a deterministic rule and shows you a
            preview against the same files. If the preview matches what AI
            Sort did, save the rule and it runs on future sorts without
            calling an LLM.
          </p>

          <p>
            For users coming from Hazel, this is the bridge: you describe the
            rule the way you described it to Hazel, and Sortio builds the
            structured config under the hood. The Hazel-to-Sortio migration
            guide walks through the common Hazel patterns and their Rule
            Builder equivalents.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/docs/rules-engine"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Rules engine documentation
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/glossary/ai-file-organizer"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                What is an AI file organizer (glossary)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/guides/migrate-from-hazel-to-sortio"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Migrate from Hazel to Sortio (rule translator)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try both on the same folder</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              Sortio Free includes both AI Sort (a one-time AI trial
              allowance) and Rule Builder (up to 5 rules). Run them on the
              same folder to see how the trade-offs play out for your files.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AiSortVsRuleBuilderBlogPage;
